/**
 * The edit dialog, as the page mounts it: the full-field form inside the
 * shared shell, one column.
 *
 * Same name and props as the dialog it replaces, so the page's Edit action
 * needs no change. When the server could not store some of what was sent,
 * the dialog stays open long enough to say which fields, rather than closing
 * on a "saved" that was only partly true.
 */
import { useState } from 'react'

import { recordKindLabel, type CrmContact } from '@/services/crm'
import ContactEditForm from './ContactEditForm'
import DialogShell from './DialogShell'
import { ignoredNote } from './crmFields'

export default function EditContactDialog({
  contact,
  onClose,
  onOpenContact,
}: {
  contact: CrmContact
  onClose: () => void
  /** Where "Open existing" goes on an address clash; optional. */
  onOpenContact?: (id: string) => void
}) {
  const [note, setNote] = useState<string | null>(null)
  return (
    <DialogShell label={`Edit ${contact.displayName}`} onClose={onClose} width="2xl">
      {note ? (
        <div className="flex flex-1 flex-col gap-3 px-4 py-4">
          <p className="text-[12px] text-stone-200">Saved.</p>
          <p className="text-[11px] text-amber-200">{note}</p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-amber-400/30 bg-amber-400/[0.12] px-2.5 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-400/[0.2] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
            >
              Done
            </button>
          </div>
        </div>
      ) : (
        <ContactEditForm
          contact={contact}
          layout="stack"
          onCancel={onClose}
          onOpenContact={onOpenContact}
          onSaved={(_patch, ignored) => {
            const text = ignoredNote(ignored, recordKindLabel(contact.id))
            if (text) setNote(text)
            else onClose()
          }}
        />
      )}
    </DialogShell>
  )
}
